import * as React from 'react';
import { Badge, StatusBadge } from './Badge';
import { Card, CardContent, CardHeader, CardTitle } from './Card';
import { Stack } from './Layout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './Table';
import { Text } from './Text';

export interface DriverCallHistoryCall {
  id: string;
  /** When the call was created, as an ISO string; printed in the viewer's locale. */
  createdAt: string;
  customer: string;
  location: string;
  vehicle: string;
  /** Already worked out — "1h 12m", "N/A". */
  timeOnCall: string;
  status: string;
  /** An archived call shows as such and cannot be opened. */
  archived?: boolean;
}

export interface DriverCallHistoryViewProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** In the order to show them — the caller sorts. */
  calls: DriverCallHistoryCall[];
  /** A row was clicked. Never called for an archived call. */
  onOpen: (id: string) => void;
}

/**
 * The calls a driver has taken, newest first, each row opening the call.
 *
 *   <DriverCallHistoryView calls={calls} onOpen={(id) => navigate(id)} />
 *
 * Archived rows stay in the list for the record but are disabled: they
 * carry `aria-disabled`, a title saying why, and never fire `onOpen`.
 */
export const DriverCallHistoryView = React.forwardRef<
  HTMLDivElement,
  DriverCallHistoryViewProps
>(function DriverCallHistoryView({ calls, onOpen, ...props }, ref) {
  return (
    <Card ref={ref} {...props}>
      <CardHeader>
        <CardTitle>Call History</CardTitle>
      </CardHeader>
      <CardContent>
        <Table bordered={false}>
          <TableHeader>
            <TableRow>
              <TableHead>DATE/TIME</TableHead>
              <TableHead>CUSTOMER</TableHead>
              <TableHead>LOCATION</TableHead>
              <TableHead>VEHICLE</TableHead>
              <TableHead>TIME ON CALL</TableHead>
              <TableHead>STATUS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {calls.map((call) => {
              const archived = Boolean(call.archived);
              const created = new Date(call.createdAt);
              return (
                <TableRow
                  key={call.id}
                  interactive={!archived}
                  data-archived={archived || undefined}
                  aria-disabled={archived}
                  title={
                    archived
                      ? 'Archived calls cannot be opened from call history'
                      : undefined
                  }
                  className={
                    archived ? 'cursor-not-allowed opacity-80' : undefined
                  }
                  onClick={() => {
                    if (!archived) onOpen(call.id);
                  }}
                >
                  <TableCell>
                    <Stack gap={1}>
                      <Text variant="body-strong">
                        {created.toLocaleDateString()}
                      </Text>
                      <Text variant="body-sm" tone="muted">
                        {created.toLocaleTimeString()}
                      </Text>
                    </Stack>
                  </TableCell>
                  <TableCell>{call.customer}</TableCell>
                  <TableCell>{call.location}</TableCell>
                  <TableCell>{call.vehicle}</TableCell>
                  <TableCell>{call.timeOnCall}</TableCell>
                  <TableCell>
                    {archived ? (
                      <Badge tone="neutral">Archived</Badge>
                    ) : (
                      <StatusBadge status={call.status} label={call.status} />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
});
